import type { GameMode } from '@roe/shared';
import { MultiplayerService } from '../../services/MultiplayerService.js';
import { GameStateManager } from '../../state/GameStateManager.js';
import { MultiplayerLobbyBase } from './MultiplayerLobbyBase.js';

export const MAX_COOP_SLOTS = 3;
const COOP_THEME_COLOR = 0x5a7030;
const COOP_BACKGROUND_KEY = 'zone_background_forest';

/**
 * Concrete Coop variant. The save slot is selected on the main menu and
 * passed via `init({ saveSlot })`. On scene create, the lobby ensures a
 * matching CoopSaveInfo exists for that slot — auto-creating one (seeded
 * with the solo save's hero classes) if missing — then the base class
 * renders the left-side save card. No in-lobby picker.
 */
export class CoopLobbyScene extends MultiplayerLobbyBase {
  protected readonly mode: GameMode = 'coop';

  private gsm = GameStateManager.getInstance();

  constructor() {
    super('CoopLobbyScene');
  }

  protected getThemeColor(): number {
    return COOP_THEME_COLOR;
  }

  protected getModeLabel(): string {
    return '🤝  Co-op Lobby';
  }

  protected getBackgroundKey(): string {
    return COOP_BACKGROUND_KEY;
  }

  /** Override async: call base create() first, then ensure a coop save row
   *  exists for the selected slot. Fire-and-await so the card re-renders
   *  with the created save. Non-fatal on failure — the card still renders
   *  from the solo save's heroes (coop auto-create is a convenience). */
  async create(): Promise<void> {
    super.create();
    if (this.selectedSaveSlot !== undefined) {
      await this.ensureCoopSaveExists(this.selectedSaveSlot);
    }
  }

  /** Auto-create a matching CoopSaveInfo for the selected slot, seeded with
   *  the solo save's hero classes. Idempotent: fetches the existing coop
   *  save list and only creates when the slot is missing. */
  private async ensureCoopSaveExists(slot: number): Promise<void> {
    const mp = MultiplayerService.getInstance();
    try {
      const existing = await mp.fetchCoopSaves();
      if (Array.isArray(existing) && existing.some((s) => s.slot === slot)) return;
    } catch (err) {
      console.warn('[CoopLobby] fetchCoopSaves failed during ensureCoopSaveExists:', err);
      // Fall through and try to create anyway — fetch failures shouldn't
      // block the auto-create path.
    }
    const info = this.gsm.getSaveSlotInfo(slot);
    // Build canonical v2 entries from the local solo save's hero data —
    // heroData carries heroKey for unique-character heroes (e.g. Valka =
    // archer/amazon, Lootfi = warrior/dwarf). Previously this code mapped
    // to bare class strings, dropping heroKey at the persistence boundary
    // → opponent card always rendered the generic class sprite (Bug B).
    const v2Heroes = (info?.heroData ?? []).map((h) => ({
      heroId: h.heroKey ?? h.heroClass,
      heroClass: h.heroClass,
      heroKey: h.heroKey,
      name: h.name,
      level: h.level,
    }));
    try {
      await mp.createCoopSave(slot, v2Heroes);
    } catch (err) {
      console.warn('[CoopLobby] createCoopSave failed — lobby continues without a coop save row:', err);
    }
  }

  protected getQueuePayload(): { mode: GameMode; coopSaveSlot: number } | undefined {
    if (this.selectedSaveSlot === undefined) return undefined;
    return { mode: 'coop', coopSaveSlot: this.selectedSaveSlot };
  }
}
